import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";

const PROCEDURE_CATEGORIES = [
  {
    id: "corpo",
    title: "Corpo",
    description: "Contorno corporal, definição e harmonização",
    mainItems: [
      "Lipoaspiração de Definição (HD)",
      "Abdominoplastia",
      "Mini-abdominoplastia",
      "Cirurgias Pós-bariátrica",
    ],
    moreItems: [
      "Lipoaspiração de Braços e Pernas",
      "Tratamento de Lipedema",
      "MILA e MIRELA",
      "Abdominoplastia Reversa",
      "Correção de diástase abdominal",
      "Correção de hérnias abdominais",
      "Lipoaspiração masculina",
      "Flancoplastia",
      "Gluteoplastia",
      "Lipoescultura",
      "Lipoenxertia",
      "Ninfoplastia",
      "Correção de cicatrizes",
      "Braquioplastia",
      "Cruroplastia",
      "Bodylift 360°",
    ],
    accent: true,
    span: "col-span-1 md:col-span-2",
  },
  {
    id: "mama",
    title: "Mama",
    description: "Procedimentos mamários com recuperação rápida",
    mainItems: [
      "Mamoplastia de Aumento com Prótese",
      "Mastopexia com Cicatrizes Reduzidas",
      "Mamoplastia Redutora",
      "Tratamento de Ginecomastia",
    ],
    moreItems: [],
    accent: false,
    span: "col-span-1",
  },
  {
    id: "face",
    title: "Face",
    description: "Rejuvenescimento e harmonização facial",
    mainItems: [
      "Lifting Facial",
      "Blefaroplastia",
      "Cervicoplastia (Pescoço/Papada)",
      "Liplift",
    ],
    moreItems: [
      "Bichectomia",
      "Browlift e Castanhares",
      "Nanofat",
      "Cantopexia",
    ],
    accent: false,
    span: "col-span-1",
  },
  {
    id: "tecnologias",
    title: "Tecnologias",
    description: "Recursos de última geração aplicados caso a caso",
    mainItems: [
      "Renuvion",
      "Bodytite",
      "Morpheus",
      "Argoplasma e Argon4",
    ],
    moreItems: [
      "Lipoaspiração ultrassônica",
      "Retraction",
      "Facetite",
      "Ignite RF",
      "Vibrolipoaspirador",
      "Laser",
    ],
    accent: false,
    span: "col-span-1",
  },
  {
    id: "cosmiatria",
    title: "Cosmiatria",
    description: "Tratamentos complementares para qualidade da pele, prevenção e rejuvenescimento",
    mainItems: [
      "Toxina botulínica",
      "Preenchimentos e harmonização",
      "Bioestimuladores",
      "Skinbooster",
    ],
    moreItems: [
      "Peeling",
      "Microagulhamento",
      "Medicina Regenerativa",
      "PDRN",
      "Exossomos",
      "PRP",
      "Fios de PDO",
      "Protocolos personalizados",
    ],
    accent: false,
    span: "col-span-1",
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const ExpandableCard = ({ cat, scrollToForm }) => {
  const [expanded, setExpanded] = useState(false);
  const hasMore = cat.moreItems.length > 0;

  return (
    <motion.div
      variants={cardVariants}
      className={`${cat.span} ${
        cat.accent
          ? "bg-navy text-white p-7 md:p-10"
          : "bg-white border border-navy/[0.04] p-6 md:p-8"
      }`}
      data-testid={`procedure-${cat.id}`}
    >
      <div className="flex items-start justify-between mb-5">
        <h3
          className={`font-heading text-2xl md:text-3xl font-normal ${
            cat.accent ? "text-white" : "text-navy"
          }`}
        >
          {cat.title}
        </h3>
        <button
          onClick={scrollToForm}
          className={`flex items-center gap-1 font-body text-[11px] font-medium tracking-[0.1em] uppercase transition-colors duration-300 ${
            cat.accent
              ? "text-gold/80 hover:text-gold"
              : "text-gold hover:text-navy"
          }`}
          data-testid={`procedure-cta-${cat.id}`}
        >
          Saiba mais <ArrowRight size={12} />
        </button>
      </div>

      <p
        className={`font-body text-sm mb-6 leading-relaxed ${
          cat.accent ? "text-white/60" : "text-slate_blue/85"
        }`}
      >
        {cat.description}
      </p>

      <ul className="space-y-2.5">
        {cat.mainItems.map((item) => (
          <li
            key={item}
            className={`font-body text-sm flex items-start gap-2.5 ${
              cat.accent ? "text-white/85" : "text-navy/80"
            }`}
          >
            <span className="text-gold mt-1.5 text-[5px]">&#9679;</span>
            {item}
          </li>
        ))}
        {expanded &&
          cat.moreItems.map((item) => (
            <li
              key={item}
              className={`font-body text-sm flex items-start gap-2.5 ${
                cat.accent ? "text-white/75" : "text-navy/70"
              }`}
            >
              <span className="text-gold mt-1.5 text-[5px]">&#9679;</span>
              {item}
            </li>
          ))}
      </ul>

      {hasMore && (
        <button
          onClick={() => setExpanded(!expanded)}
          className={`mt-5 flex items-center gap-1.5 font-body text-[11px] font-medium tracking-[0.08em] uppercase transition-colors duration-300 ${
            cat.accent
              ? "text-white/40 hover:text-white/60"
              : "text-navy/35 hover:text-navy/55"
          }`}
          data-testid={`procedure-expand-${cat.id}`}
        >
          {expanded ? "Ver menos" : `Ver todos (${cat.moreItems.length}+)`}
          <ChevronDown
            size={14}
            className={`transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
          />
        </button>
      )}
    </motion.div>
  );
};

export const ProceduresSection = () => {
  const scrollToForm = () => {
    const el = document.querySelector("#contato");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="procedimentos"
      data-testid="procedures-section"
      className="py-28 md:py-40 bg-[#F3F3F3]"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="mb-16 md:mb-20">
          <p className="font-body text-gold text-xs font-medium tracking-[0.35em] uppercase mb-4">
            Especialidades
          </p>
          <h2 className="font-heading text-navy text-4xl md:text-5xl font-medium leading-tight max-w-2xl">
            Procedimentos
          </h2>
          <div className="gold-line mt-6" />
        </div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
        >
          {PROCEDURE_CATEGORIES.map((cat) => (
            <ExpandableCard key={cat.id} cat={cat} scrollToForm={scrollToForm} />
          ))}
        </motion.div>
      </div>
    </section>
  );
};
